import React from "react"
import PropTypes from "prop-types"
import {
    Autocomplete,
    TextField,
    InputAdornment,
    IconButton,
    CircularProgress,
} from "@mui/material"
import {styled, useTheme} from "@mui/material/styles"
import {filterAutocompleteOptions} from "src/v2/utils/autocomplete/filter-autocomplete-options"

const StyledAutoCompleteField = styled(Autocomplete, {
    shouldForwardProp: (prop) => prop !== "labelLeft",
})(({theme, labelLeft = 32}) => ({
    "& .MuiOutlinedInput-root": {
        borderColor: "#fff",
        backgroundColor: theme.palette.input?.background?.white05 || "#FFFFFF0D",
        color: theme.palette.primary?.contrastText || "#000",
        borderRadius: 0,
        border: `1px solid ${theme.palette.input?.borderColor?.primary || "#ccc"}`,
        padding: "0px 9px 0 9px",

        "&:hover": {
            backgroundColor: theme.palette.input?.background?.secondary || "#f5f5f5",
            border: `1px solid ${theme.palette.input?.borderColor.white || "#FFFFFF"}`,
        },

        "&.Mui-focused:not(.Mui-error)": {
            backgroundColor: theme.palette.input?.background?.secondary || "#f5f5f5",
            border: `1px solid ${theme.palette.input?.background.white || "#FFFFFF"}`,
        },

        "&.Mui-error": {
            backgroundColor: theme.palette.input?.background?.primary || "#fff",
            border: `1px solid ${theme.palette.input?.borderColor?.warning || theme.palette.error?.main || "#d32f2f"}`,
        },

        "& svg": {
            color: theme.palette.icon?.main || theme.palette.action?.active || "#666",
        },

        "&.Mui-error svg": {
            color: theme.palette.icon?.error || theme.palette.error?.main || "#d32f2f",
        },

        "& input:-webkit-autofill": {
            backgroundColor: "rgba(210, 159, 9, 0.3) !important",
            transition: "background-color 5000s ease-in-out 0s",
            WebkitTextFillColor: `${theme.palette.primary?.contrastText || "#000"} !important`,
            caretColor: `${theme.palette.primary?.contrastText || "#000"} !important`,
        },
    },

    "& .MuiInputBase-input": {
        color: theme.palette.common?.white || "#000",
        fontSize: theme.typography.fontSizes?.sm || "14px",
        paddingLeft: 0,
        paddingRight: 0,
        lineHeight: 1.4,
    },

    "& .MuiInputLabel-root.Mui-focused": {
        color: theme.palette.common?.white || "#000",
        opacity: 0.7,
        transform: "translate(14px, -6px) scale(0.75)",
        transformOrigin: "top left",
    },

    "& .MuiInputLabel-root": {
        color: theme.palette.common?.white || "#000",
        opacity: 0.7,
        fontSize: "14px",
        left: labelLeft,
        transformOrigin: "top left",
    },

    "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: 0,
        border: "0 !important",
    },

    "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
        padding: "22px 4px 10.5px 5px",
    },

    "& .MuiFormLabel-filled.MuiInputLabel-root": {
        top: 14,
    },

    "& .Mui-focused.MuiInputLabel-root": {
        top: 14,
    },

    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
        transform: "translate(14px, -6px) scale(0.75)",
        transformOrigin: "top left",
    },

    "& .MuiInputAdornment-root": {
        marginTop: "0 !important",
        alignItems: "center",
        display: "flex",
        height: "100%",

        "& svg": {
            display: "block",
            verticalAlign: "middle",
            margin: "auto",
            color: theme.palette.icon?.main || theme.palette.action?.active || "#666",
        },
    },

    "& .MuiFormHelperText-root": {
        marginTop: 8,
        marginLeft: 0,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeights?.regular || 400,
        fontStyle: "normal",
        fontSize: theme.typography.fontSizes?.sm || "14px",
        lineHeight: "100%",
        letterSpacing: "0%",
        color: theme.palette.text?.warning || theme.palette.error?.main || "#d32f2f",
    },
}))

const getComparableValue = (option) => {
    if (typeof option === "string" || typeof option === "number") return option

    return option?.value ??
        option?.id ??
        option?.CPFCNPJ ??
        option?.formattedCPFCNPJ
}

const InputAutoComplete = React.memo(
    ({
        label,
        startIcon,
        endIcon,
        onEndIconClick = () => {},
        error = false,
        helperText = "",
        value,
        inputValue,
        options = [],
        loading = false,
        loadingText = "Buscando...",
        onChange,
        onInputChange,
        filterOptions: incomingFilterOptions,
        minCharsToFilter = 0,
        filterKeys,
        normalizeFilter,
        getOptionLabel: customGetOptionLabel,
        isOptionEqualToValue: customIsOptionEqualToValue,
        renderOption: customRenderOption,
        getOptionDisabled,
        labelLeft = 32,
        noOptionsText,
        freeSolo = false,
        ...props
    }) => {
        const theme = useTheme()

        const getOptionLabel = React.useCallback(
            (option) => {
                if (customGetOptionLabel) return customGetOptionLabel(option)
                if (!option) return ""
                if (typeof option === "string") return option

                const name = option?.label || option?.nome || option?.name || ""
                const doc = option?.value || option?.cpf_cnpj || option?.cpfCnpj || ""

                return [name, doc].filter(Boolean).join(" ").trim()
            },
            [customGetOptionLabel]
        )

        const isOptionEqualToValue = React.useCallback(
            (option, currentValue) => {
                if (customIsOptionEqualToValue) {
                    return customIsOptionEqualToValue(option, currentValue)
                }

                const optionValue = getComparableValue(option)
                const selectedValue = getComparableValue(currentValue)

                if (optionValue === undefined || optionValue === null ||
                    selectedValue === undefined || selectedValue === null) {
                    return option === currentValue
                }

                return String(optionValue) === String(selectedValue)
            },
            [customIsOptionEqualToValue]
        )

        const customFilterOptions = React.useMemo(() => {
            if (typeof incomingFilterOptions === "function") {
                return (availableOptions, params) => {
                    const input = String(params?.inputValue ?? "").trim()

                    if (minCharsToFilter > 0 && input.length < minCharsToFilter) {
                        return []
                    }

                    return incomingFilterOptions(availableOptions, params)
                }
            }

            if (!filterKeys?.length &&
                typeof normalizeFilter !== "function" &&
                minCharsToFilter <= 0) {
                return undefined
            }

            return (availableOptions, params) => filterAutocompleteOptions(
                availableOptions,
                params?.inputValue,
                {
                    filterKeys,
                    getOptionLabel,
                    normalizeFilter,
                    minCharsToFilter,
                }
            )
        }, [
            filterKeys,
            getOptionLabel,
            incomingFilterOptions,
            minCharsToFilter,
            normalizeFilter,
        ])

        const selectedOption = React.useMemo(() => {
            if (value === undefined || value === null || value === "") return null
            return options.find((option) => isOptionEqualToValue(option, value)) || null
        }, [isOptionEqualToValue, options, value])

        const autocompleteValue = selectedOption ||
            (freeSolo || typeof value === "object" ? value ?? null : null)

        const derivedInputValue = inputValue ?? (
            selectedOption
                ? getOptionLabel(selectedOption)
                : value
                    ? getOptionLabel(value)
                    : ""
        )

        const computedNoOptionsText = React.useMemo(() => {
            if (typeof noOptionsText === "function") {
                return noOptionsText(derivedInputValue)
            }

            if (typeof noOptionsText === "string") {
                return derivedInputValue
                    ? `${noOptionsText} ${derivedInputValue}`
                    : noOptionsText
            }

            return derivedInputValue
                ? `Não cadastrado ${derivedInputValue}`
                : "Nenhum resultado encontrado"
        }, [derivedInputValue, noOptionsText])

        const renderInput = React.useCallback(
            (params) => (
                <TextField
                    {...params}
                    fullWidth
                    label={label}
                    error={error}
                    helperText={error ? helperText : undefined}
                    variant="outlined"
                    InputLabelProps={{
                        shrink: Boolean(derivedInputValue) ||
                            Boolean(value) ||
                            params.inputProps?.value?.length > 0,
                    }}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: startIcon ? (
                            <InputAdornment position="start" sx={{pointerEvents: "none"}}>
                                {React.cloneElement(startIcon, {
                                    style: {width: 24, height: 24},
                                })}
                            </InputAdornment>
                        ) : params.InputProps.startAdornment,
                        endAdornment: loading ? (
                            <InputAdornment position="end">
                                <CircularProgress size={20} />
                            </InputAdornment>
                        ) : (
                            <>
                                {endIcon && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={onEndIconClick}
                                            edge="end"
                                            size="small"
                                        >
                                            {React.cloneElement(endIcon, {
                                                style: {
                                                    color: theme.palette.icon?.main ||
                                                        theme.palette.action?.active ||
                                                        "#666",
                                                    width: 24,
                                                    height: 24,
                                                },
                                            })}
                                        </IconButton>
                                    </InputAdornment>
                                )}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            ),
            [
                derivedInputValue,
                value,
                label,
                error,
                helperText,
                startIcon,
                endIcon,
                onEndIconClick,
                loading,
                theme,
            ]
        )

        return (
            <StyledAutoCompleteField
                disablePortal={false}
                noOptionsText={computedNoOptionsText}
                loadingText={loadingText}
                fullWidth
                options={options}
                value={autocompleteValue}
                inputValue={derivedInputValue}
                loading={loading}
                freeSolo={freeSolo}
                onChange={onChange}
                onInputChange={onInputChange}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={isOptionEqualToValue}
                filterOptions={customFilterOptions}
                renderOption={customRenderOption}
                renderInput={renderInput}
                labelLeft={labelLeft}
                getOptionDisabled={getOptionDisabled}
                {...props}
            />
        )
    }
)

InputAutoComplete.displayName = "InputAutoComplete"

InputAutoComplete.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.any,
    inputValue: PropTypes.string,
    options: PropTypes.array,
    loading: PropTypes.bool,
    loadingText: PropTypes.string,
    startIcon: PropTypes.element,
    endIcon: PropTypes.element,
    onEndIconClick: PropTypes.func,
    onChange: PropTypes.func,
    onInputChange: PropTypes.func,
    error: PropTypes.bool,
    helperText: PropTypes.string,
    getOptionLabel: PropTypes.func,
    isOptionEqualToValue: PropTypes.func,
    renderOption: PropTypes.func,
    getOptionDisabled: PropTypes.func,
    filterOptions: PropTypes.func,
    minCharsToFilter: PropTypes.number,
    filterKeys: PropTypes.arrayOf(PropTypes.string),
    normalizeFilter: PropTypes.func,
    labelLeft: PropTypes.number,
    noOptionsText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    freeSolo: PropTypes.bool,
}

export default InputAutoComplete